import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Sparkles, Loader2, Image as ImageIcon, Send, FileText,
  CheckCircle2, AlertTriangle, Clock, ChevronDown, ChevronUp,
  Layers, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAIContentEngine = ({ adminFetch }) => {
  // Single article generator state
  const [peptideName, setPeptideName] = useState('');
  const [productSlug, setProductSlug] = useState('');
  const [category, setCategory] = useState('Peptide Research');
  const [autoLink, setAutoLink] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedArticle, setGeneratedArticle] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState('draft');

  // Batch generator state
  const [batchInput, setBatchInput] = useState('');
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState(null);

  // Categories
  const [categories, setCategories] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    adminFetch(`${API_URL}/api/admin/ai/categories`)
      .then(r => r.json())
      .then(setCategories)
      .catch(() => {});
  }, []);

  // Poll batch status
  useEffect(() => {
    if (!batchId || !batchRunning) return;
    const interval = setInterval(async () => {
      try {
        const res = await adminFetch(`${API_URL}/api/admin/ai/batch-status/${batchId}`);
        const data = await res.json();
        setBatchStatus(data);
        if (data.status === 'completed' || data.status === 'failed') {
          setBatchRunning(false);
          if (data.status === 'completed') toast.success(`Batch complete: ${data.completed} articles generated`);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [batchId, batchRunning]);

  const pollTaskStatus = async (taskId, onComplete, onFail) => {
    const poll = async () => {
      try {
        const res = await adminFetch(`${API_URL}/api/admin/ai/task-status/${taskId}`);
        const data = await res.json();
        if (data.status === 'completed') {
          onComplete(data.result);
        } else if (data.status === 'failed') {
          onFail(data.error || 'Generation failed');
        } else {
          setTimeout(poll, 3000);
        }
      } catch {
        onFail('Failed to check status');
      }
    };
    poll();
  };

  const handleGenerateArticle = async () => {
    if (!peptideName.trim()) { toast.error('Enter a peptide name'); return; }
    setGenerating(true);
    setGeneratedArticle(null);
    setGeneratedImageUrl('');
    try {
      const res = await adminFetch(`${API_URL}/api/admin/ai/generate-article`, {
        method: 'POST',
        body: JSON.stringify({
          peptide_name: peptideName.trim(),
          product_slug: productSlug.trim(),
          category,
          auto_link: autoLink
        })
      });
      const data = await res.json();
      if (data.success && data.task_id) {
        toast.info('Generating article... This may take up to 60 seconds.');
        pollTaskStatus(
          data.task_id,
          (article) => { setGeneratedArticle(article); setGenerating(false); toast.success('Article generated successfully!'); },
          (err) => { setGenerating(false); toast.error(err); }
        );
      } else {
        toast.error(data.error || data.detail || 'Generation failed');
        setGenerating(false);
      }
    } catch (e) {
      toast.error('Failed to start article generation');
      setGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!generatedArticle?.image_prompt) { toast.error('Generate an article first'); return; }
    setGeneratingImage(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/ai/generate-image`, {
        method: 'POST',
        body: JSON.stringify({
          image_prompt: generatedArticle.image_prompt,
          peptide_name: peptideName,
          slug: generatedArticle.slug
        })
      });
      const data = await res.json();
      if (data.success && data.task_id) {
        toast.info('Generating image... This may take up to 60 seconds.');
        pollTaskStatus(
          data.task_id,
          (result) => { setGeneratedImageUrl(result.image_url); setGeneratingImage(false); toast.success('Hero image generated!'); },
          (err) => { setGeneratingImage(false); toast.error(err); }
        );
      } else {
        toast.error(data.detail || 'Image generation failed');
        setGeneratingImage(false);
      }
    } catch (e) {
      toast.error('Failed to start image generation');
      setGeneratingImage(false);
    }
  };

  const handlePublish = async () => {
    if (!generatedArticle) return;
    setPublishing(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/ai/publish-article`, {
        method: 'POST',
        body: JSON.stringify({
          article: generatedArticle,
          status: publishStatus,
          image_url: generatedImageUrl,
          notify_on_publish: publishStatus === 'published'
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Article saved as ${publishStatus}!`);
        setGeneratedArticle(null);
        setGeneratedImageUrl('');
        setPeptideName('');
        setProductSlug('');
      } else {
        toast.error(data.error || 'Failed to publish');
      }
    } catch (e) {
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleBatchGenerate = async () => {
    if (!batchInput.trim()) { toast.error('Enter peptide names'); return; }
    setBatchRunning(true);
    setBatchStatus(null);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/ai/batch-generate`, {
        method: 'POST',
        body: JSON.stringify({ peptides: batchInput.trim(), auto_link: autoLink })
      });
      const data = await res.json();
      if (data.success) {
        setBatchId(data.batch_id);
        toast.success(`Batch started: ${data.total} articles queued`);
      } else {
        toast.error(data.detail || 'Batch failed');
        setBatchRunning(false);
      }
    } catch (e) {
      toast.error('Failed to start batch');
      setBatchRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="h-6 w-6 text-violet-500" />
        <h2 className="font-heading text-2xl font-bold text-slate-900">AI Research Content Engine</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Single Article Generator */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-violet-50 to-slate-50">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-violet-600" />
              Article Generator
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div>
              <Label>Peptide Name *</Label>
              <Input value={peptideName} onChange={e => setPeptideName(e.target.value)} placeholder="e.g., BPC-157" data-testid="ai-peptide-name" />
            </div>
            <div>
              <Label>Product Slug</Label>
              <Input value={productSlug} onChange={e => setProductSlug(e.target.value)} placeholder="e.g., bpc-157" data-testid="ai-product-slug" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="ai-category-select">
                {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={autoLink} onChange={e => setAutoLink(e.target.checked)} className="rounded" />
              Auto-link related peptides
            </label>

            <div className="flex gap-2">
              <Button onClick={handleGenerateArticle} disabled={generating} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" data-testid="generate-article-btn">
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Article</>}
              </Button>
            </div>

            {generatedArticle && (
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-green-700 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> Article Ready
                  </p>
                  <button onClick={() => setShowPreview(!showPreview)} className="text-xs text-violet-600 flex items-center gap-1">
                    <Eye className="h-3 w-3" /> {showPreview ? 'Hide' : 'Preview'}
                  </button>
                </div>
                <p className="text-sm font-medium text-slate-800">{generatedArticle.title}</p>
                <p className="text-xs text-slate-500">{generatedArticle.meta_description}</p>
                <div className="flex flex-wrap gap-1">
                  {generatedArticle.secondary_keywords?.slice(0, 5).map((kw, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleGenerateImage} disabled={generatingImage} variant="outline" className="flex-1" data-testid="generate-image-btn">
                    {generatingImage ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><ImageIcon className="h-4 w-4 mr-2" />Generate Image</>}
                  </Button>
                </div>

                {generatedImageUrl && (
                  <div className="rounded-lg overflow-hidden border border-slate-200">
                    <img src={`${API_URL}${generatedImageUrl}`} alt="Hero" className="w-full h-40 object-cover" />
                  </div>
                )}

                <div className="flex gap-2 items-center">
                  <select value={publishStatus} onChange={e => setPublishStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="ai-publish-status">
                    <option value="draft">Save as Draft</option>
                    <option value="published">Publish Now</option>
                  </select>
                  <Button onClick={handlePublish} disabled={publishing} className="flex-1 bg-teal-700 hover:bg-teal-800 text-white" data-testid="publish-article-btn">
                    {publishing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Send className="h-4 w-4 mr-2" />{publishStatus === 'published' ? 'Publish' : 'Save Draft'}</>}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Batch Generator */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-amber-50 to-slate-50">
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-5 w-5 text-amber-600" />
              Batch Article Generator
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div>
              <Label>Peptide List (comma separated)</Label>
              <textarea
                value={batchInput}
                onChange={e => setBatchInput(e.target.value)}
                placeholder="BPC-157, TB-500, GHK-Cu, CJC-1295, Thymosin Alpha 1"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm min-h-[100px] resize-y"
                data-testid="batch-peptide-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              Each peptide will get: article, SEO metadata, internal links, FAQ. Saved as Draft. Images can be generated individually after.
            </p>
            <Button onClick={handleBatchGenerate} disabled={batchRunning} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="batch-generate-btn">
              {batchRunning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Layers className="h-4 w-4 mr-2" />Generate Batch</>}
            </Button>

            {batchStatus && (
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3" data-testid="batch-status-panel">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-slate-800">Batch Progress</span>
                  <Badge className={batchStatus.status === 'completed' ? 'bg-green-100 text-green-700' : batchStatus.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                    {batchStatus.status}
                  </Badge>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-violet-600 h-2 rounded-full transition-all"
                    style={{ width: `${((batchStatus.completed + batchStatus.failed) / batchStatus.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  {batchStatus.completed} completed, {batchStatus.failed} failed of {batchStatus.total}
                </p>
                {batchStatus.results?.length > 0 && (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {batchStatus.results.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs px-2 py-1 bg-white rounded border border-slate-100">
                        <span className="font-medium">{r.peptide}</span>
                        {r.status === 'success' ? (
                          <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Done</span>
                        ) : (
                          <span className="text-red-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Failed</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Article Preview */}
      {showPreview && generatedArticle && (
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Article Preview</CardTitle>
              <button onClick={() => setShowPreview(false)} className="text-sm text-slate-500">Close</button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {generatedImageUrl && (
              <img src={`${API_URL}${generatedImageUrl}`} alt="Hero" className="w-full h-64 object-cover rounded-lg mb-6" />
            )}
            <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: generatedArticle.article_html }} />
            {generatedArticle.faq?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  {generatedArticle.faq.map((f, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-4">
                      <p className="font-medium text-slate-800">{f.question}</p>
                      <p className="text-sm text-slate-600 mt-1">{f.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminAIContentEngine;
